import { z } from 'zod';
import { Op, applyOps } from './ops.js';
/**
 * Was noch nicht beim Server ist (S28). Sascha: „Nach einem Absturz dort
 * weitermachen, wo man war.“
 *
 * **Gespeichert wird längst automatisch** -- jeder Schnitt-Schritt geht über
 * die Leitung und liegt als Zeile beim Server; es gibt keinen Speichern-Knopf,
 * weil es nichts zu drücken gibt. Nur: *wenn die Leitung gerade nicht steht*,
 * ging der Schritt bis zum 17. September verloren. Und zwar lautlos -- auf dem
 * Schirm war er getan, im Rückgängig stand er, beim Server nie. Beim nächsten
 * Verbinden kam der Stand des Servers und räumte ihn weg.
 *
 * Also eine Warteschlange: was nicht hinausging, bleibt hier, überlebt das
 * Neuladen und geht hinaus, sobald die Leitung wieder steht.
 *
 * **Die Grenzen sind Absicht:**
 *
 *  * **Höchstens 200 Pakete.** Wer eine halbe Stunde offline schneidet, hat
 *    ein anderes Problem als eine Warteschlange -- und ein Speicher, der
 *    unbegrenzt wächst, sprengt irgendwann den Browser. Was nicht mehr
 *    hineinpasst, fällt **vorn** heraus und wird gemeldet: ein stiller Verlust
 *    ist genau der Fehler, den diese Datei behebt.
 *  * **Höchstens zwölf Stunden alt.** Ein Schritt von gestern auf einen
 *    Schnitt, an dem seither drei Leute gearbeitet haben, ist keine Rettung,
 *    sondern ein Schaden. Was zu alt ist, geht deshalb nicht von selbst
 *    hinaus -- aber auch nicht verloren: seit 7.220 wird gefragt.
 *
 * Ohne Browser gerechnet, damit sich beides prüfen lässt.
 */
export const OFFEN_MAX = 200;
export const OFFEN_ALTER_MS = 12 * 3600 * 1000;
export const OffenesPaket = z.object({
    /** Die Nummer, unter der es hinausgeht -- dieselbe, die der Server zurückmeldet. */
    seq: z.number().int().positive(),
    /** Wann es entstanden ist, als Zeitstempel. */
    wann: z.number().int(),
    ops: z.array(Op).min(1),
});
export const Warteschlange = z.object({
    projectId: z.string(),
    pakete: z.array(OffenesPaket).default([]),
});
export const leereSchlange = (projectId) => ({ projectId, pakete: [] });
/** Ein Paket anstellen. */
export function anstellen(schlange, paket) {
    const pakete = [...schlange.pakete, paket];
    const zuViele = Math.max(0, pakete.length - OFFEN_MAX);
    return { schlange: { ...schlange, pakete: pakete.slice(zuViele) }, verloren: zuViele };
}
/**
 * Alles bis zu dieser Nummer ist angekommen.
 *
 * `<=` und nicht `===`: der Server bestätigt in der Reihenfolge, in der er
 * verarbeitet, und eine Bestätigung, die eine ältere überholt, hat sie
 * mitbestätigt. Sonst blieben Pakete für immer stehen.
 */
export function bestaetigt(schlange, seq) {
    return { ...schlange, pakete: schlange.pakete.filter((p) => p.seq > seq) };
}
/**
 * Was älter ist als erlaubt, gezählt in Schritten, und seit wann (7.220).
 *
 * Bis zum 4. Oktober flog es beim Verbinden hinaus, mit einer Meldung, die
 * acht Sekunden stand. Sascha: „werden bereiche nicht gespeichert? Weil wenn
 * ich es nun auf dem desktop öffne sind sie weg“ -- ein Telefon, das das
 * Projekt erst am nächsten Tag wieder öffnet, verlor so alles, was es nicht
 * losgeworden war. Jetzt wird gefragt; verworfen wird nur, wer es will.
 */
export function alteSchritte(schlange, jetzt, alterMs = OFFEN_ALTER_MS) {
    const alt = schlange.pakete.filter((p) => jetzt - p.wann > alterMs);
    if (alt.length === 0)
        return { schritte: 0, seit: null };
    return { schritte: alt.reduce((n, p) => n + p.ops.length, 0), seit: Math.min(...alt.map((p) => p.wann)) };
}
/**
 * Der eigene Stand beim Öffnen: der des Servers, und darüber, was noch
 * aussteht (7.220). Ohne das zeigte ein neu geladenes Telefon den Stand des
 * Servers -- die wartenden Schritte gingen danach zwar hinaus, aber ihr Echo
 * gilt als eigenes und wird nicht noch einmal angewandt. Sie waren beim
 * Server und nirgends auf dem Schirm.
 */
export function mitOffenem(timeline, schlange) {
    return applyOps(timeline, schlange.pakete.flatMap((p) => p.ops));
}
/** Was zu alt ist, fliegt hinaus -- und wird gezählt, nicht verschwiegen. */
export function aufraeumen(schlange, jetzt, alterMs = OFFEN_ALTER_MS) {
    const frisch = schlange.pakete.filter((p) => jetzt - p.wann <= alterMs);
    return { schlange: { ...schlange, pakete: frisch }, verjaehrt: schlange.pakete.length - frisch.length };
}
/**
 * Was dazu auf dem Schirm steht.
 *
 * Gezählt werden die **Schritte** und nicht die Pakete: „3 Änderungen“ ist
 * eine Auskunft, „2 Pakete“ ist eine Ausrede.
 */
export function offenSatz(schlange) {
    const schritte = schlange.pakete.reduce((summe, p) => summe + p.ops.length, 0);
    if (schritte === 0)
        return null;
    return schritte === 1 ? 'Eine Änderung ist noch nicht gesichert.' : `${schritte} Änderungen sind noch nicht gesichert.`;
}
/**
 * Ob der Stand des Servers den eigenen ersetzen darf.
 *
 * **Nein, solange etwas aussteht.** Genau daran gingen die Schritte verloren:
 * der Server schickt beim Verbinden seinen Stand, und der war „neuer“, weil
 * die eigene Nummer gar nicht mitgewachsen ist. Erst wenn die Schlange leer
 * ist, ist der Stand des Servers der vollständigere.
 */
export function serverStandUebernehmen(schlange, serverRev, eigeneRev) {
    if (schlange.pakete.length > 0)
        return false;
    return serverRev >= eigeneRev;
}
//# sourceMappingURL=warteschlange.js.map